import {Kpi} from '@hconnect/common/types'
import {dataTestId, useNotification} from '@hconnect/uikit'
import {DeleteOutlined} from '@mui/icons-material'
import {Box, IconButton, ToggleButton, ToggleButtonGroup} from '@mui/material'
import {isArray, isNil} from 'lodash'
import React, {FC, useEffect, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {ControlledAutocomplete} from '../../../../../common/components'
import {invalidateSettingsQuery} from '../../../../../common/providers'
import {submitOnBlurAndEnterProps} from '../../../../../common/utils'
import {useEditJanusKeyFigure} from '../../hooks/useEditJanusKeyFigure'
import {JanusSpecificKpi} from '../../types'

const TIME_STEP_BUTTON_CONFIG: {value: number; label: string}[] = [
  {
    value: 1,
    label: 'kpiCalculationSettings.label.daily'
  },
  {
    value: 2,
    label: 'kpiCalculationSettings.label.monthly'
  }
]
const FIELD_WIDTH = {xs: '100%', md: '60%'}

type JanusKeyFiguresFormProps = {
  kpi: Kpi
  plantKpis: JanusSpecificKpi[] | undefined
  setNewKeyFigure: (keyFigure: JanusSpecificKpi | undefined) => void
  keyFigure: Partial<JanusSpecificKpi>
  upmId: string | undefined
  unitId: string | null
}

export const JanusKeyFiguresForm: FC<JanusKeyFiguresFormProps> = ({
  kpi,
  plantKpis,
  setNewKeyFigure,
  keyFigure,
  upmId,
  unitId
}) => {
  const {t} = useTranslation()
  const {notify} = useNotification()

  const figureSelectedKpi = useMemo(
    () => plantKpis?.find((pk) => pk.id === keyFigure.id),
    [plantKpis, keyFigure.id]
  )

  const defaultKpiValue = useMemo(() => {
    if (figureSelectedKpi) {
      return {
        id: figureSelectedKpi.id,
        label: figureSelectedKpi.displayName,
        periodType: figureSelectedKpi.janusKpi.kpiPeriodTypeId
      }
    }

    return figureSelectedKpi ?? {id: '', label: '', periodTypeId: 1}
  }, [figureSelectedKpi])

  const {mutateAsync: editKeyFigureAsync} = useEditJanusKeyFigure()
  const [frequency, setFrequency] = useState<{value: number; label: string} | undefined>()

  const {
    handleSubmit,
    control,
    watch,
    formState: {isDirty}
  } = useForm<{
    specificKpi: {id: string; label: string; periodTypeId: number}
  }>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      specificKpi: defaultKpiValue
    }
  })

  const selectedKpi = watch('specificKpi')

  useEffect(() => {
    const periodType = selectedKpi.periodTypeId ?? figureSelectedKpi?.janusKpi?.kpiPeriodTypeId ?? 1
    setFrequency(periodType === 1 ? TIME_STEP_BUTTON_CONFIG[0] : TIME_STEP_BUTTON_CONFIG[1])
  }, [figureSelectedKpi, selectedKpi])

  const deleteKpisDefinition = async (
    shownSuccessNotification: boolean,
    plantUpmId: string | undefined,
    specificKpiId: string | undefined
  ) => {
    if (!upmId || !specificKpiId || !plantUpmId) return

    const result = await editKeyFigureAsync({
      kpiDefinitionId: null,
      plantUpmId,
      specificKpiId
    })

    setNewKeyFigure(undefined)
    if (shownSuccessNotification) {
      notify('success', t('success.editJanusKeyFigure'))
    }
    invalidateSettingsQuery('janusSpecificKpisByDefinition')

    return result
  }

  useEffect(() => {
    if (unitId && keyFigure.id) {
      if (Number(unitId) !== keyFigure.unitTypeId) {
        void deleteKpisDefinition(false, upmId, keyFigure.id)
      }
    }
  }, [figureSelectedKpi, keyFigure.id])

  const updateKpiDefinition = async (
    kpiDefinitionId: number | null,
    plantUpmId: string | undefined,
    specificKpiId: string | undefined
  ) => {
    if (!upmId || !specificKpiId || !plantUpmId) return

    try {
      await deleteKpisDefinition(false, upmId, keyFigure.id).then(() => {
        void editKeyFigureAsync({
          kpiDefinitionId,
          plantUpmId,
          specificKpiId
        }).then(() => {
          notify('success', t('success.editJanusKeyFigure'))
          setNewKeyFigure(undefined)
          invalidateSettingsQuery('janusSpecificKpisByDefinition')
        })
      })
    } catch {
      notify('error', t('error.janusKpiEdit'))
    }
  }

  const handleDeleteKpi = async () => {
    if (!keyFigure.id) {
      setNewKeyFigure(undefined)
      return
    }
    if (upmId && keyFigure?.id) {
      try {
        await deleteKpisDefinition(false, upmId, keyFigure.id).then(() => {
          notify('success', t('success.editJanusKeyFigure'))
        })
      } catch {
        notify('error', t('error.janusKpiEdit'))
      }
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    if (!isDirty || !upmId || !data.specificKpi.id) return
    await updateKpiDefinition(kpi.kpiDefinitionId, upmId, data.specificKpi.id)
  })

  return (
    <Box
      {...dataTestId(`janus-edit-kpi-figures-form-${keyFigure.id ?? 'new'}`)}
      display="flex"
      flexDirection="row"
      gap={1}
    >
      <Box width={FIELD_WIDTH}>
        <ControlledAutocomplete
          formDataName="specificKpi"
          label={t('kpiCalculationSettings.label.keyFigure')}
          control={control}
          options={
            plantKpis?.map((pk) => ({
              id: pk.id,
              label: pk.displayName,
              periodTypeId: pk.janusKpi.kpiPeriodTypeId
            })) || []
          }
          getOptionLabel={(option) =>
            isNil(option) ? '' : isArray(option) ? option.join(', ') : option.label
          }
          disableClearable
          onSubmit={onSubmit}
          {...submitOnBlurAndEnterProps(onSubmit)}
          {...dataTestId(`specific-kpi-autocomplete-${keyFigure.id ?? 'new'}`)}
        />
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexDirection={'row'}
      >
        <ToggleButtonGroup
          fullWidth
          color="primary"
          exclusive
          value={frequency?.value || TIME_STEP_BUTTON_CONFIG[0].value}
        >
          {TIME_STEP_BUTTON_CONFIG.map(({value, label}) => (
            <ToggleButton
              sx={{cursor: 'default'}}
              key={value}
              value={value}
              {...dataTestId(`time-step-${value}-${keyFigure.id ?? 'new'}`)}
            >
              {t(label)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <IconButton>
          <DeleteOutlined
            color="error"
            onClick={handleDeleteKpi}
            {...dataTestId(`delete-janus-key-figure-button-${keyFigure.id ?? 'new'}`)}
          />
        </IconButton>
      </Box>
    </Box>
  )
}
